import React, { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import Image from 'src/components/ui/Image/Image';
import {
  useReturnsFormContext,
  ReturnFormTrackingStepValues,
  returnFormTrackingStepSchema,
  returnFormProductsStepSchema,
} from './ReturnsFormContext';
import OrdersSearchTable from '../../components/layouts/OrdersSearchTable/OrdersSearchTable';
import Icon from '../../components/ui/Icon/Icon';
import Divider from '../../components/ui/Divider/Divider';
import { useSaleOrdersQuery } from '../../services/saleOrder';
import { useReturnPickingProductsQuery } from '../../services/returns';
import ProductsSearchTable from '../../components/layouts/ProductsSearchTable/ProductsSearchTable';
import ReadOnlyPartnerInfo from './ReadonlyPartnerInfo';
import SearchBar from '../../components/ui/TableOverview/Components/SearchBar';
import { useDataViewController } from '../../hooks/useDataViewController';

export const Section: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => {
  return <div className={`p-24 rounded-base surface-elevated border-subtle ${className ?? ''}`}>{children}</div>;
};

interface SectionHeadlineProps {
  children: React.ReactNode;
  tag?: keyof JSX.IntrinsicElements;
}

export const SectionHeadline: React.FC<SectionHeadlineProps> = ({ children, tag = 'h2' }) => {
  const Tag = tag;
  return <Tag className="typo-title-24">{children}</Tag>;
};

export const ActionBar: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <div className="flex justify-end gap-x-12">{children}</div>;
};

export const ReturnsConfiguratorForm = forwardRef<
  {
    triggerSubmit: () => void;
  },
  {
    onSubmit?: () => void;
    step?: number;
    setDisableNextButton?: (disableButton: boolean) => void;
  }
>(({ onSubmit: onSubmitCb, step, setDisableNextButton }, ref) => {
  const { formState, setFormState } = useReturnsFormContext();

  const { t } = useTranslation();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const productsTableRef = React.useRef<{ submitForm: (callback?: () => void) => void }>(null);
  const readonlyProductsTableRef = React.useRef<{ submitForm: (callback?: () => void) => void }>(null);

  const controller = useDataViewController();

  const combinedDomain = useMemo(() => {
    // Shipping feedback from WMS/carrier, not fulfillment_state: an open case
    // turns the latter into "under_investigation", even for delivered orders.
    const base = ['synced_state', 'in', ['shipped', 'delivered']];
    const search = controller.search?.trim();
    if (!search) return JSON.stringify([base]);

    const isNumeric = !Number.isNaN(Number(search));
    const conditions: any[] = [
      ['name', 'ilike', search],
      ['partner_id.name', 'ilike', search],
    ];
    if (isNumeric) {
      conditions.push(['id', '=', Number(search)]);
    }

    // Build OR chain in Odoo polish notation: N conditions need N-1 "|" operators
    const orChain: any[] = [];
    for (let i = 0; i < conditions.length - 1; i++) orChain.push('|');
    orChain.push(...conditions);

    return JSON.stringify(['&', base, ...orChain]);
  }, [controller.search]);
  const { data: saleOrders } = useSaleOrdersQuery({
    domain: combinedDomain,
  });

  const { data: returnableProducts } = useReturnPickingProductsQuery(formState.tracking.orders[0]?.id, {
    enabled: formState.tracking.orders.length > 0,
  });
  const allReturnableProducts = returnableProducts?.returnable_product_quantity.length
    ? returnableProducts?.returnable_product_quantity
    : [];

  const {
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ReturnFormTrackingStepValues>({
    resolver: zodResolver(returnFormTrackingStepSchema(t)),
    defaultValues: formState.tracking,
  });

  const onSubmit = (data: any) => {
    setFormState(prev => ({
      ...prev,
      tracking: data,
    }));
    onSubmitCb?.();
  };

  useImperativeHandle(ref, () => ({
    triggerSubmit: () => {
      if (step === 2 && productsTableRef.current) {
        productsTableRef.current.submitForm(() => {
          const validationResult = returnFormProductsStepSchema(t).safeParse(formState.products);

          if (validationResult.success) {
            onSubmitCb?.();
          }
        });
      } else {
        handleSubmit(onSubmit)();
      }
    },
  }));

  const handleOrdersChange = (selectedOrders: string[]) => {
    const selectedItems = selectedOrders.map(index => saleOrders?.items[Number(index)]).filter(Boolean);

    if (setDisableNextButton) {
      setDisableNextButton(selectedItems.length === 0);
    }

    const updatedOrders = formState.tracking.orders.filter(order =>
      selectedItems.some((item: any) => item.name === order.name),
    );

    selectedItems.forEach(item => {
      if (!updatedOrders.some((existingOrder: any) => existingOrder.name === item?.name)) {
        updatedOrders.push(item);
      }
    });

    if (JSON.stringify(updatedOrders) !== JSON.stringify(formState.tracking.orders)) {
      setFormState(prev => ({
        ...prev,
        tracking: {
          ...prev.tracking,
          orders: updatedOrders,
        },
      }));
    }
  };

  useEffect(() => {
    if (formState) {
      reset(formState.tracking);
    }
  }, [formState, reset]);

  const handleProductsChange = (data: any[]) => {
    if (!setDisableNextButton) return;
    if (step !== 2) return;

    const invalid =
      data.length === 0 ||
      data.some(item => {
        const productFromBE = allReturnableProducts.find(p => p.product_id === item.product_id);
        const maxQty = productFromBE?.quantity ?? 0;
        return !item.quantity || item.quantity <= 0 || item.quantity > maxQty || !item.reason_id;
      });

    setDisableNextButton(invalid);
    setFormState(prev => ({
      ...prev,
      products: {
        data,
      },
    }));
  };
  const tableData = useMemo(() => {
    if (!formState.products.data) return [];

    return allReturnableProducts
      .filter(product => formState.products.data.some(item => item.product_id === product.product_id))
      .map(product => {
        const returnItem = formState.products.data.find(item => item.product_id === product.product_id)!;
        return {
          ...product,
          quantity: returnItem.quantity,
          reason_id: returnItem.reason_id,
        };
      });
  }, [formState.products.data, allReturnableProducts]);

  const carriers = React.useMemo(() => {
    const seen = new Set<number>();

    return (formState.tracking.orders || [])
      .map(order => order?.carrier)
      .filter((carrier): carrier is NonNullable<typeof carrier> => {
        if (!carrier?.id || seen.has(carrier.id)) return false;

        seen.add(carrier.id);
        return true;
      });
  }, [formState.tracking.orders]);

  const uniquePartners = React.useMemo(() => {
    const seen = new Set<number | string>();

    return (formState.tracking.orders || []).reduce<any[]>((acc, order) => {
      const partner = order?.partner;
      if (!partner) return acc;

      const key = partner.id ?? partner.name;
      if (seen.has(key)) return acc;

      seen.add(key);
      acc.push(partner);
      return acc;
    }, []);
  }, [formState.tracking.orders]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="grid gap-y-32">
        <div className="flex flex-col items-start gap-[12px]">
          <SectionHeadline>{t('returns.order')}</SectionHeadline>
          {step === 1 && <span className="typo-text-14 text-faded">{t('returns.search_description')}</span>}
        </div>
        {step === 1 && (
          <SearchBar
            value={controller.search}
            onChange={controller.setSearch}
            placeholder={t('general.search_placeholder')}
          />
        )}

        {errors.orders && (
          <div className="typo-text-14 flex gap-4 status-error-full justify-center">
            <Icon name="error" />
            <div className="content-center">{errors.orders.message || errors.orders.root?.message}</div>
          </div>
        )}
        {step === 1 && saleOrders && (
          <div ref={containerRef}>
            <OrdersSearchTable containerRef={containerRef} data={saleOrders} onSelectionChange={handleOrdersChange} />
          </div>
        )}
        {step === 2 && (
          <div className="flex flex-col gap-[12px]">
            <div ref={containerRef}>
              <OrdersSearchTable containerRef={containerRef} data={formState.tracking.orders} readonly />
              <Divider className="mb-[20px] mt-[32px]" />
            </div>
            <SectionHeadline>{t('general.products')}</SectionHeadline>
            <span className="typo-text-14 text-faded">{t('returns.products_description')}</span>
            <ProductsSearchTable
              ref={productsTableRef}
              containerRef={containerRef}
              data={allReturnableProducts}
              onSelectionChange={handleProductsChange}
            />
          </div>
        )}
        {step === 3 && (
          <div className="flex flex-col gap-[12px]">
            <div className="flex flex-col gap-[24px]">
              <div className="flex flex-col gap-6">
                <span className="typo-text-14--bold">{t('general.carrier')}</span>

                <div className="flex items-center gap-12 flex-wrap">
                  {carriers.length > 0 ? (
                    carriers.map(carrier =>
                      carrier.logo_url ? (
                        <Image
                          key={carrier.id}
                          imageUrl={carrier.logo_url}
                          alt={carrier.name}
                          className="h-[24px] w-auto object-contain"
                          fallbackComponent={<span className="typo-text-14 text-faded">{carrier.name}</span>}
                        />
                      ) : (
                        <span key={carrier.id} className="typo-text-14 text-faded">
                          {carrier.name}
                        </span>
                      ),
                    )
                  ) : (
                    <span className="typo-text-14 text-faded">{t('returns.no_carrier_set')}</span>
                  )}
                </div>
              </div>

              {/* 🔹 Receiver Section */}
              <div className="flex flex-col gap-16">
                <SectionHeadline>{t('general.receiver')}</SectionHeadline>

                {uniquePartners.map((partner, index) => (
                  <ReadOnlyPartnerInfo key={partner.id ?? index} partner={partner} />
                ))}
              </div>
            </div>

            <Divider className="mb-[20px] mt-[32px]" />

            <SectionHeadline>{t('general.products')}</SectionHeadline>
            <span className="typo-text-14 text-faded">{t('returns.products_description')}</span>

            <ProductsSearchTable
              ref={readonlyProductsTableRef}
              containerRef={containerRef}
              data={tableData}
              onSelectionChange={() => {}}
              readonly
            />
          </div>
        )}
      </div>
    </form>
  );
});
